'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import GatedSeedStartingNotes from '@/components/GatedSeedStartingNotes';
import SeedStartingDateResults from '@/components/SeedStartingDateResults';
import { useUserData } from '@/context/UserDataContext';
import type { CanonicalPlant } from '@/lib/normalizePlant';
import {
  getPlantCatalogSearchRelevance,
  getPlantSourceLabel,
  listCanonicalPlantCatalog,
  plantMatchesCatalogSearch,
  sortCanonicalPlants,
} from '@/lib/plantCatalog';
import { calculateSeedStartingDates } from '@/lib/seedStartingCalculator';
import { getPlantingReadiness } from '@/lib/plantingReadiness';

function matchesPlantParam(plant: CanonicalPlant, param: string): boolean {
  const normalized = param.trim().toLowerCase();
  return (
    plant.id.toLowerCase() === normalized ||
    plant.name.toLowerCase() === normalized ||
    plant.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') === normalized
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  );
}

export default function SeedStartingCalculatorContent() {
  const searchParams = useSearchParams();
  const plantParam = searchParams.get('plant');
  const { zone, weatherData } = useUserData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlantId, setSelectedPlantId] = useState<string | null>(null);
  const [allPlants, setAllPlants] = useState<CanonicalPlant[]>([]);
  const [isLoadingCommunity, setIsLoadingCommunity] = useState(true);
  const [prefillApplied, setPrefillApplied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadCommunityPlants() {
      setIsLoadingCommunity(true);
      const catalog = await listCanonicalPlantCatalog({ includeDraftGenerated: true });

      if (cancelled) return;
      setAllPlants(catalog);
      setIsLoadingCommunity(false);
    }

    void loadCommunityPlants();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!plantParam || prefillApplied || allPlants.length === 0) return;
    const match = allPlants.find(plant => matchesPlantParam(plant, plantParam));
    if (!match) return;
    setSelectedPlantId(match.id);
    setSearchTerm(match.name);
    setPrefillApplied(true);
  }, [allPlants, plantParam, prefillApplied]);

  const selectedPlant = useMemo(
    () => allPlants.find(plant => plant.id === selectedPlantId) ?? null,
    [allPlants, selectedPlantId],
  );

  const searchResults = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return allPlants.slice(0, 8);

    return allPlants
      .filter(plant => plantMatchesCatalogSearch(plant, query))
      .sort((left, right) => {
        const scoreDelta =
          getPlantCatalogSearchRelevance(right, query).score -
          getPlantCatalogSearchRelevance(left, query).score;
        if (scoreDelta !== 0) return scoreDelta;
        return sortCanonicalPlants(left, right);
      })
      .slice(0, 12);
  }, [allPlants, searchTerm]);

  const calculation = selectedPlant
    ? calculateSeedStartingDates(selectedPlant, zone)
    : null;
  const readinessWindow = calculation?.transplant ?? calculation?.directSow ?? null;
  const readiness = selectedPlant && calculation
    ? getPlantingReadiness({
        plant: selectedPlant,
        zone,
        weather: weatherData,
        calculatedWindow: readinessWindow,
      })
    : null;

  return (
    <div className="max-w-[900px] space-y-6">
      <div>
        <Link
          href="/plants"
          className="inline-flex items-center gap-1.5 font-body text-[14px] text-sage hover:text-warm-brown transition-colors duration-200"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 2L4 7L9 12" />
          </svg>
          Back to Plant Library
        </Link>
        <h1 className="font-heading text-[32px] text-dark-text leading-tight mt-4">
          Seed Starting Date Calculator
        </h1>
        <p className="font-body text-[16px] text-sage mt-2 max-w-[680px] leading-relaxed">
          Choose a plant to see seed-starting, transplant, and direct-sow dates based on
          your saved growing zone and the plant&apos;s existing timing guidance.
        </p>
      </div>

      <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
        <div>
          <label htmlFor="seed-starting-plant-search" className="font-body font-semibold text-[13px] text-dark-text">
            Search for a plant
          </label>
        </div>

        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sage pointer-events-none">
            <SearchIcon />
          </span>
          <input
            id="seed-starting-plant-search"
            type="text"
            value={searchTerm}
            onChange={event => {
              setSearchTerm(event.target.value);
              setSelectedPlantId(null);
            }}
            placeholder="Search tomato, lettuce, basil..."
            className="w-full rounded-xl border border-sand bg-white py-3 pl-10 pr-4 font-body text-[15px] text-dark-text outline-none transition-colors placeholder:text-[#aaa] focus:border-sage"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {searchResults.map(plant => {
            const selected = plant.id === selectedPlantId;
            const sourceLabel = getPlantSourceLabel(plant);
            return (
              <button
                key={plant.id}
                type="button"
                onClick={() => {
                  setSelectedPlantId(plant.id);
                  setSearchTerm(plant.name);
                }}
                className={[
                  'text-left rounded-lg border px-4 py-3 transition-colors',
                  selected
                    ? 'border-deep-green bg-light-sage'
                    : 'border-sand bg-white hover:border-sage',
                ].join(' ')}
              >
                <span className="block font-heading text-[17px] text-dark-text">
                  {plant.name}
                </span>
                <span className="block font-body text-[12px] text-sage mt-0.5">
                  {plant.scientificName}
                  {sourceLabel ? ` - ${sourceLabel}` : ''}
                </span>
              </button>
            );
          })}
        </div>

        {isLoadingCommunity && (
          <p className="font-body text-[13px] text-sage">
            Loading community plants...
          </p>
        )}
      </section>

      {selectedPlant && calculation ? (
        <section className="bg-white rounded-xl border border-sand p-6 space-y-5">
          <div>
            <p className="font-body text-[12px] text-sage uppercase tracking-wider font-semibold">
              Selected Plant
            </p>
            <h2 className="font-heading text-[24px] text-dark-text mt-1">
              {selectedPlant.name}
            </h2>
          </div>

          <SeedStartingDateResults calculation={calculation} readiness={readiness} showSource />

          <GatedSeedStartingNotes seedStarting={selectedPlant.seedStarting} />
        </section>
      ) : (
        <section className="bg-white rounded-xl border border-sand p-6">
          <h2 className="font-heading text-[22px] text-dark-text">
            Pick a plant to calculate dates
          </h2>
          <p className="font-body text-[15px] text-sage mt-2 leading-relaxed">
            This first version uses your USDA zone&apos;s typical last frost date and
            the timing phrases already stored on each plant, such as &ldquo;6 - 8 weeks
            before last frost&rdquo; or &ldquo;after last frost.&rdquo;
          </p>
        </section>
      )}
    </div>
  );
}
